/**
 * Every bot engine the API accepts.
 *
 * One list, because the edit form used to repeat it inline. When `self_hosted`
 * was added the copy inside the form's reset was missed, so a campaign saved on
 * that engine came back displaying "Telnyx AI Assistant" — and pressing Update
 * again would have written that wrong value back over the real one.
 */
export const BOT_ENGINES = [
  'telnyx_assistant',
  'conversation_relay',
  'vapi_direct',
  'vapi_bridge',
  'self_hosted',
] as const;

export type BotEngine = (typeof BOT_ENGINES)[number];

export function isBotEngine(value: unknown): value is BotEngine {
  return typeof value === 'string' && (BOT_ENGINES as readonly string[]).includes(value);
}

export interface Campaign {
  id: string;
  name: string;
  description?: string;
  callDescription: string;
  promptScript: string;
  interestedQuestions?: string[];
  captureSchema: CaptureField[];
  // Owner information
  ownerType: 'user' | 'organization';
  ownerId: string;
  ownerEmail?: string;
  ownerName?: string;
  // Assistant configuration
  assistantFirstMessage?: string;
  assistantFirstMessageMode?: 'assistant-speaks-first' | 'assistant-waits-for-user';
  scripting?: Record<string, any>;
  campaignType?: 'bot' | 'human' | 'text';
  botEngine?: BotEngine;
  /** Custom AI pipeline v2 — ElevenLabs + Deepgram + stage engine (admin toggle). */
  botPipelineV2?: boolean;
  telnyxAssistantId?: string | null;
  textConfig?: {
    templateBody?: string;
    warmupStartVolume?: number;
    warmupEndVolume?: number;
    warmupDays?: number;
    intervalBaseMinutes?: number;
    eligibleLeadStatuses?: string[];
    sendSchedules?: Array<{
      startTime: string;
      endTime: string;
      timezone: string;
    }>;
  };
  dialingType?: 'auto' | 'manual';
  agentIds?: string[];
  scriptId?: string | null;
  // Phone numbers for calls
  phoneNumberIds?: string[];
  // Conversation parameters
  personalityType?: string;
  persuasionApproach?: string;
  timezone: string;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  maxRetries: number;
  retryDelayMinutes: number;
  timezonePrioritizationEnabled?: boolean;
  areaCodePriorityEnabled?: boolean;
  callsPerAgent?: number | null;
  dialRatio?: number | null;
  dialRatioMin?: number | null;
  dialRatioMax?: number | null;
  pacingPausedUntil?: string | null;
  leadShuffleEnabled?: boolean;
  dialTimezoneFilter?: string | null;
  /** Bot capacity dialer — wave-based active-conversation targeting. */
  botDialingMode?: 'legacy' | 'capacity';
  botTargetActiveConversations?: number;
  botMaxActiveConversations?: number;
  botWaveSize?: number;
  botWaveIntervalMs?: number;
  /** Multi dial windows: hours on Eastern Time clock; slot timezone = lead bucket filter. Empty = anytime. */
  voiceDialSchedules?: Array<{
    startTime: string;
    endTime: string;
    timezone: string;
  }>;
  status: CampaignStatus;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  pausedAt?: string;
  stats?: CampaignStats;
  conversationStyles?: ConversationStyle[];
}

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed' | 'cancelled' | 'trash';

export interface ConversationStyle {
  id: string;
  name: string;
  conversationStyle: 'natural' | 'professional' | 'casual';
  personalityType: 'curious' | 'helpful' | 'friendly';
  persuasionApproach: 'gentle' | 'consultative' | 'direct';
  transcriber?: 'deepgram' | '11labs' | 'assembly-ai' | 'azure' | 'gladia' | 'google' | 'openai';
  isActive: boolean;
  enhancedPromptScript?: string;
  selectedVoiceId?: string;
  voiceConfig?: any;
  createdAt: Date;
  updatedAt: Date;
}

/** Create/edit form row — `id` is absent until the style has been saved. */
export type ConversationStyleInput = Omit<ConversationStyle, 'id' | 'createdAt' | 'updatedAt'> & {
  id?: string | undefined;
};

export function isFishVoiceId(id?: string | null): boolean {
  const trimmed = String(id || '').trim().toLowerCase();
  return trimmed.startsWith('fish.') || trimmed.startsWith('fish:');
}

export function voiceMatchesSelfHostedTts(
  selectedVoiceId: string | undefined,
  tts: 'elevenlabs' | 'fish'
): boolean {
  const id = String(selectedVoiceId || '').trim();
  if (!id) return false;
  return tts === 'fish' ? isFishVoiceId(id) : !isFishVoiceId(id);
}

/**
 * Fish and ElevenLabs store different id shapes. Mixing them on one campaign
 * (or leaving an ElevenLabs hash after switching the picker to Fish) sends the
 * wrong TTS on the call.
 */
export function conversationStylesVoiceError(
  botEngine: string | undefined,
  styles: Array<{ isActive?: boolean; selectedVoiceId?: string | null }> | undefined
): string | null {
  if (!styles?.length) return null;
  const active = styles.filter((style) => style.isActive);

  if (botEngine !== 'self_hosted') {
    if (active.some((style) => isFishVoiceId(style.selectedVoiceId))) {
      return 'Fish Audio voices only work on Full Custom AI (self-hosted). Switch the bot engine or pick an ElevenLabs voice.';
    }
    return null;
  }

  if (active.some((style) => !String(style.selectedVoiceId || '').trim())) {
    return 'Each active conversation style needs a voice that matches the TTS provider (ElevenLabs or Fish Audio).';
  }

  const fishCount = active.filter((style) => isFishVoiceId(style.selectedVoiceId)).length;
  if (fishCount > 0 && fishCount < active.length) {
    return 'Active styles must all use Fish Audio or all use ElevenLabs. Those voice IDs are not interchangeable.';
  }
  return null;
}

export interface CaptureField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'email' | 'phone' | 'select' | 'multiselect' | 'boolean';
  required: boolean;
  options?: string[];
  placeholder?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export interface CampaignStats {
  totalLeads: number;
  remainingLeads: number;
  newLeads: number;
  calledLeads: number;
  interestedLeads: number;
  notInterestedLeads: number;
  noAnswerLeads: number;
  unreachableLeads: number;
  followUpLeads: number;
  outOfBusinessLeads: number;
  wrongNumberLeads: number;
  failedLeads: number;
  doNotCallLeads: number;
  totalCalls: number;
  callAttempts: number;
  activeCalls?: number;
  successfulCalls: number;
  failedCalls: number;
  averageCallDuration: number;
  interestRate: number;
  completionRate: number;
  lastCallAt?: string;
}

export interface CreateCampaignData {
  name: string;
  description?: string;
  callDescription: string;
  promptScript: string;
  interestedQuestions?: string[];
  captureSchema: CaptureField[];
  // Assistant configuration
  assistantFirstMessage?: string;
  assistantFirstMessageMode?: 'assistant-speaks-first' | 'assistant-waits-for-user';
  scripting?: Record<string, any>;
  campaignType?: 'bot' | 'human' | 'text';
  botEngine?: BotEngine;
  /** Custom AI pipeline v2 — ElevenLabs + Deepgram + stage engine (admin toggle). */
  botPipelineV2?: boolean;
  telnyxAssistantId?: string | null;
  textConfig?: {
    templateBody?: string;
    warmupStartVolume?: number;
    warmupEndVolume?: number;
    warmupDays?: number;
    intervalBaseMinutes?: number;
    eligibleLeadStatuses?: string[];
    sendSchedules?: Array<{
      startTime: string;
      endTime: string;
      timezone: string;
    }>;
  };
  dialingType?: 'auto' | 'manual';
  agentIds?: string[];
  scriptId?: string | null;
  // Phone numbers for calls
  phoneNumberIds?: string[];
  // Conversation parameters
  conversationStyle?: string;
  personalityType?: string;
  persuasionApproach?: string;
  timezone: string;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  maxRetries?: number;
  retryDelayMinutes?: number;
  timezonePrioritizationEnabled?: boolean;
  areaCodePriorityEnabled?: boolean;
  leadShuffleEnabled?: boolean;
  dialTimezoneFilter?: string | null;
  voiceDialSchedules?: Array<{
    startTime: string;
    endTime: string;
    timezone: string;
  }>;
  conversationStyles?: ConversationStyleInput[];
}

export interface CampaignResultFilters {
  startDateTime?: Date;
  endDateTime?: Date;
}

export interface UpdateCampaignData extends Partial<CreateCampaignData> {
  status?: CampaignStatus;
}

export interface AssistantConfig {
  id: string;
  name: string;
  assistantPayload: Record<string, any>;
  voiceId: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  voice?: Voice;
}

export interface Voice {
  id: string;
  name: string;
  provider: string;
  providerId: string;
  language: string;
  gender: 'male' | 'female' | 'neutral';
  accent?: string;
  style?: string;
  previewUrl?: string;
}

export type PhoneNumberPurpose = 'manual' | 'text' | 'ai' | 'auto' | 'follow_up';

export type PhoneNumberAssignedCarrier = 'tmobile' | 'att' | 'verizon' | 'all' | 'unknown';

export interface VapiPhoneNumber {
  id: string;
  number: string;
  provider: string;
  providerId?: string;
  name?: string;
  assistantId?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Local phone_numbers.id when number is on Vapi Telnyx connection */
  localPhoneNumberId?: string;
  connectionName?: string;
}

export interface PhoneNumber {
  id: string;
  number: string;
  provider: string;
  providerId?: string;
  smsCapable?: boolean;
  messagingProfileId?: string;
  /** Line type the number is reserved for; null = unassigned (usable anywhere). */
  purpose?: PhoneNumberPurpose | null;
  /** Predictive dialer CID carrier pool. */
  assignedCarrier?: PhoneNumberAssignedCarrier;
  /** Telnyx SIP / Call Control connection display name */
  connectionName?: string;
  /** Local phone_numbers.id (Vapi picker legacy ID support) */
  localPhoneNumberId?: string;
  /** True when number is on TELNYX_CONNECTION_ID (AI-Calling-Agent) */
  connectionOk?: boolean;
  needsConnectionFix?: boolean;
  name?: string;
  assistantId?: string;
  serverUrl?: string;
  serverUrlSecret?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CampaignFilters {
  status?: CampaignStatus[];
  excludeStatus?: CampaignStatus;
  search?: string;
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'status';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface CampaignPermanentDeleteStatus {
  campaignId: string;
  queueJobId: string | null;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progressPercent: number;
  totalLeads: number;
  deletedLeads: number;
  totalCallAttempts: number;
  deletedCallAttempts: number;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  updatedAt: string;
}